import Button from "@/Components/Button/Button";
import Counter from "@/Components/Counter/Counter";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import usePosMenuGetPriceHook from "@/shared/hooks/usePosMenuGetPriceHook";
import {
  useAddCartItem,
  useCart,
  useReplaceCartItem,
} from "@/shared/hooks/useCartHook";
import { getFieldByLang, formatPrice } from "@/shared/utils/helpers";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { List, useListRef } from "react-window";
import Modal, { ModalProps } from "../Modal/Modal";
import { CartItem } from "@/shared/utils/interfaces";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import { MenuItems, ToppingContentTypes } from "@/shared/utils/enum";
import PosToppingItem from "../PosMenu/PosToppingItem";

const ROW_HEADER_HEIGHT = 20;
const ROW_ERROR_HEIGHT = 20;
const ROW_GAP = 5;
const TOPPING_ROW_HEIGHT = 80;
const COLUMNS_MOBILE = 2;
const COLUMNS_DESKTOP = 4;

interface ToppingGroupProps {
  garnish: CartItem & { topping_content_type?: number };
  selectedToppings: any[];
  onToppingChange: (garnishId: number, toppings: any[]) => void;
  isMobile: boolean;
}

interface ListRowProps {
  formData: Record<number, any[]>;
  errors: Record<number, string>;
  handleToppingChange: (garnishId: number, toppings: any[]) => void;
  product: CartItem;
  isMobile: boolean;
}

const ToppingGroup = memo(
  ({
    garnish,
    selectedToppings,
    onToppingChange,
    isMobile,
  }: ToppingGroupProps) => {
    const isSingleSelect = [
      ToppingContentTypes.MANDATORY,
      ToppingContentTypes.OPTIONAL,
    ].includes(garnish.topping_content_type);

    const handleToppingClick = (topping: CartItem) => {
      const isSelected = selectedToppings?.some(
        (t) => t.item_id === topping.item_id
      );

      if (!isSelected) {
        const newValue = {
          label: topping.name,
          item_id: topping.item_id,
          name: topping.name,
          price: topping.price,
          vat_percentage: topping.vat_percentage,
        };

        onToppingChange(
          garnish.item_id,
          isSingleSelect ? [newValue] : [...selectedToppings, newValue]
        );
      } else {
        const newValue = selectedToppings.filter(
          ({ item_id }) => item_id !== topping.item_id
        );
        onToppingChange(garnish.item_id, newValue.length > 0 ? newValue : []);
      }
    };

    const columns = isMobile ? COLUMNS_MOBILE : COLUMNS_DESKTOP;

    return (
      <div
        className="grid gap-[0.5rem]"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {garnish?.sub_content?.map((topping: CartItem, index: number) => {
          const isSelected = selectedToppings?.some(
            (t) => t.item_id === topping.item_id
          );

          return (
            <PosToppingItem
              key={topping.item_id + index}
              topping={topping}
              index={index}
              onClick={() => handleToppingClick(topping)}
              isSelected={isSelected}
            />
          );
        })}
      </div>
    );
  }
);

const RowComponent = ({
  index,
  style,
  ...data
}: {
  index: number;
  style: React.CSSProperties;
} & ListRowProps) => {
  const garnish = data.product.sub_content[index];

  return (
    <div className="mb-[1rem]" style={style}>
      <div className="font-semibold text-[1.6rem]">
        {getFieldByLang(garnish.name)}
      </div>
      <ToppingGroup
        garnish={garnish}
        selectedToppings={data.formData[garnish.item_id] || []}
        onToppingChange={data.handleToppingChange}
        isMobile={data.isMobile}
      />
      {data.errors[garnish.item_id] && (
        <div className="mt-[0.5rem] text-[1.8rem] text-error font-semibold">
          {data.errors[garnish.item_id]}
        </div>
      )}
    </div>
  );
};

const PosOrderSelectToppingsModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobileHook();
  const [{ product, isEdit, index }] = useModalMeta();
  const [quantity, setQuantity] = useState(1);
  const [formData, setFormData] = useState<Record<number, any[]>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [cart] = useCart();
  const setModal = useSetModal();
  const getPrice = usePosMenuGetPriceHook();
  const listRef = useListRef();
  const addCartItem = useAddCartItem();
  const replaceCartItem = useReplaceCartItem();

  useEffect(() => {
    const initialSelectedToppings = cart[index]?.selected_sub_content ?? [];
    if (initialSelectedToppings?.length > 0) {
      const parentIds = initialSelectedToppings
        .map((t: CartItem) => t.parent_id)
        .filter((id: number) => Boolean(id));

      const uniqueParentIds: number[] = Array.from(new Set(parentIds));

      const defaultValues: Record<number, any[]> = {};
      uniqueParentIds.forEach((parentId) => {
        defaultValues[parentId] = initialSelectedToppings.filter(
          (t) => t.parent_id === parentId
        );
      });

      setFormData(defaultValues);
    }
  }, [cart, index]);

  const handleToppingChange = useCallback(
    (garnishId: number, toppings: any[]) => {
      setFormData((prev) => ({
        ...prev,
        [garnishId]: toppings,
      }));

      if (toppings.length > 0) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[garnishId];
          return newErrors;
        });
      }
    },
    []
  );

  const validateForm = (): boolean => {
    const newErrors: Record<number, string> = {};
    let firstErrorIndex: number | null = null;

    product.sub_content.forEach((garnish, index: number) => {
      if (garnish.topping_content_type === ToppingContentTypes.MANDATORY) {
        const selectedToppings = formData[garnish.item_id] || [];
        if (selectedToppings.length === 0) {
          if (firstErrorIndex === null) firstErrorIndex = index;
          newErrors[garnish.item_id] = t("fieldRequired");
        }
      }
    });

    setErrors(newErrors);

    if (firstErrorIndex !== null) {
      listRef.current?.scrollToRow({
        index: firstErrorIndex,
        align: "start",
        behavior: "smooth",
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const subContent = Object.keys(formData).map((key) => {
      return (formData[+key] || []).map((item) => ({
        name: item?.name,
        price: item?.price,
        item_id: item?.item_id,
        quantity: quantity,
        parent_id: +key,
        vat_percentage: item.vat_percentage,
        item_type: MenuItems.TOPPING,
      }));
    });

    const newProduct = {
      ...product,
      quantity: quantity,
      selected_sub_content: [...subContent.flat(1)],
    };

    isEdit ? replaceCartItem(newProduct, index) : addCartItem(newProduct);
    setModal("", {});
  };

  const getToppingsTotalPrice = useMemo(() => {
    return Object.values(formData)
      ?.flat(1)
      ?.reduce((acc, cur) => acc + parseFloat(cur?.price || "0"), 0);
  }, [formData]);

  const getItemSize = useCallback(
    (index: number) => {
      const garnish = product.sub_content[index];
      const toppingsCount = garnish?.sub_content?.length || 0;
      const columns = isMobile ? COLUMNS_MOBILE : COLUMNS_DESKTOP;
      const rows = Math.ceil(toppingsCount / columns);

      return (
        ROW_HEADER_HEIGHT +
        ROW_ERROR_HEIGHT +
        rows * (TOPPING_ROW_HEIGHT + ROW_GAP) +
        rows * ROW_GAP
      );
    },
    [product, isMobile]
  );

  const rowProps: ListRowProps = useMemo(
    () => ({
      formData,
      errors,
      handleToppingChange,
      product: product,
      isMobile,
    }),
    [formData, errors, handleToppingChange, product, isMobile]
  );

  return (
    <Modal
      width={{ _: "90%", md: "100rem" }}
      className="w-[90%] md:w-200"
      onExit={onExit}
      {...props}
    >
      <div className="font-bold mb-1 text-[1.8rem]">
        {getFieldByLang(product.name)}
      </div>

      <List<ListRowProps>
        listRef={listRef}
        rowCount={product.sub_content.length}
        rowHeight={(i) => getItemSize(i)}
        rowComponent={RowComponent}
        rowProps={rowProps}
        overscanCount={1}
        style={{ height: 450, width: "100%" }}
      />

      <div className="mt-8 flex justify-between gap-4">
        <div className="flex w-3/4 items-center justify-between">
          <div className="font-semibold">
            {formatPrice(
              (parseFloat(getPrice(product)) + getToppingsTotalPrice) * quantity
            )}
          </div>
          <Counter
            value={quantity}
            onDecreaseClick={() => quantity > 1 && setQuantity(quantity - 1)}
            onIncreaseClick={() => setQuantity(quantity + 1)}
            variant={isMobile ? "medium" : "large"}
          />
        </div>
        <Button type="button" onClick={onSubmit}>
          {t("posOrders.save")}
        </Button>
      </div>
    </Modal>
  );
};

export default PosOrderSelectToppingsModal;
